import React, { useState, useRef } from 'react';
import useClickOutside from '../../hooks/useClickOutside';
import classNames from 'classnames';
import styles from './AutoComplete.module.scss';
import DropdownOption from '../../interfaces/DropdownOption';
import TextArea from '../TextArea/TextArea';
import {AUTOCOMPLETE} from "@kmx/legos-react-text-field/dist/types";

type AutoCompleteProps = {
    /**
     * Controls whether and how the text value should be automatically capitalized as it is entered/edited by the user
     *
     * @default none
     */
    autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';

    /**
     * Controls whether form inputs can be auto filled by the user's browser
     *
     * @default on
     */
    autoComplete?: AUTOCOMPLETE;
    /**
     * Additional CSS class(es) to add to the `<TextField />`
     */
    className?: string;

    /**
     * Attribute indicating the user cannot interact with the `<TextField />`
     *
     * @default false
     */
    disabled?: boolean;

    /**
     * Used to determine the string value for a given option. It's used to fill the input
     *
     */
    getOptionLabel: (option: DropdownOption) => string;

    /**
     * Phrase to render as input control ‘help’ message
     */
    helperText?: string;

    /**
     * Phrase to render as input is searching
     */
    searchingText?: string;

    /**
     * Phrase to render when no results are found
     */
    noResultsText?: string;

    /**
     * Always show ‘help’ message, even when control is not focused
     *
     * @default false
     */
    helperTextIsPersistent?: boolean;

    /**
     * Unique identifier for the `<TextField />` instance. You may pass your own value to override the component’s auto-generated ID.
     */
    id?: string;

    /*
     * Required accessible label associated with this input control.
     */
    label: string;

    /**
     * Attribute indicating that there is something loading for the `<TextField />`
     *
     * @default false
     */
    loading?: boolean;

    /**
     * The maximum number of characters that the user can enter
     */
    maxLength?: number;

    /**
     * Name of the `<input>`
     */
    name?: string;

    /**
     * Array of options.
     */
    options: DropdownOption[];

    /**
     * Boolean attribute indicating the `<input>` should be required
     *
     * @default false
     */
    required?: boolean;

    /**
     * Value of the `<input>`
     */
    value: string;

    /**
     * Previous selection from dropdown to help uphold state on off click
     */
    previousSelection?: string;

    /**
     * Previous search term to help uphold state on off click
     */
    previousSearch?: string;

    /**
     * Validation status of the `<input>`
     */
    validationStatus?: 'valid' | 'invalid';

    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onSelection: (option: DropdownOption) => void;
    onClickOutside?: () => void;
};

export default function AutoComplete({
    autoCapitalize,
    autoComplete = 'on',
    className,
    disabled,
    getOptionLabel,
    helperText,
    helperTextIsPersistent,
    id,
    label,
    loading,
    name,
    onBlur,
    onChange,
    onFocus,
    onSelection,
    options,
    required,
    value,
    validationStatus,
    searchingText = 'Searching...',
    noResultsText = 'No results found',
    onClickOutside,
}: AutoCompleteProps) {
    const autoCompleteRef = useRef<HTMLInputElement>(null);
    const [isShown, setIsShown] = useState(false);

    useClickOutside(autoCompleteRef, () => {
        if (isShown) {
            if (onClickOutside) {
                onClickOutside();
            }
            setIsShown(false);
        }
    });

    const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        if (onFocus) {
            onFocus(event);
        }
        setIsShown(true);
    };

    const handleOptionSelect = (option: DropdownOption) => {
        setIsShown(false);
        onSelection(option);
    };

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(event);
        if (event.target.value) setIsShown(true);
    }

    return (
        <div ref={autoCompleteRef}>
            <TextArea
                autoComplete={autoComplete}
                autoCapitalize={autoCapitalize}
                className={className}
                name={name}
                onBlur={onBlur}
                onChange={handleOnChange}
                onFocus={handleOnFocus}
                id={id}
                label={label}
                loading={loading}
                rows={2}
                disabled={disabled}
                helperText={helperText}
                helperTextIsPersistent={helperTextIsPersistent}
                type='text'
                inputMode='text'
                validationStatus={validationStatus}
                required={required}
                value={value}
            />
            {isShown && (options?.length || value?.length) ? (
                <div className={styles['dropdown']} data-cy="dropdown">
                    <ul className={styles['dropdown-content']}
                        data-cy="dropdown-content">
                        {options?.length ? (
                            options.map((option, index) => (
                                <li
                                    className={classNames(styles['option'], 'kmx-typography--body-2')}
                                    onClick={() => handleOptionSelect(option)}
                                    key={index}
                                >
                                    {getOptionLabel(option)}
                                </li>
                            ))
                        ) : value?.length ? (
                            <li
                                className={classNames(styles['message'], 'kmx-typography--body-2')}
                                key="no-results"
                                data-cy="dropdown-no-results"
                            >
                                {loading ? searchingText : noResultsText}
                            </li>
                        ) : null}
                    </ul>
                </div>
            ) : null}
        </div>
    );
}
